import { ChooseFileUploadModal } from '@components/ChooseMediaModal/ChooseFileUploadModal';
import { ChooseFileUploadModalSummaryConfirm } from '@components/ChooseMediaModal/ChooseFileUploadModalSummaryConfirm';
import {
  DataTable,
  DurationFilterInput,
  getGridNumericOperators,
  getGridSingleSelectOperators,
  GridColDef,
  GridColumnVisibilityModel,
  gridFilteredSortedRowIdsSelector,
  GridFilterModel,
  GridRenderCellParams,
  GridSortModel,
  useGridApiRef,
} from '@components/DataTable';
import { DragDropOverlay } from '@components/GlobalDragDropOverlay';
import { DragDropRejected } from '@lib/files/drag-files';
import { useFileUploadFlow } from '@lib/hooks/useFileUploadFlow';
import { useGridColumnPreferences } from '@lib/hooks/useGridColumnPreferences';
import { getYouTubeVideoId } from '@lib/utils/platforms';
import { showToast } from '@lib/utils/toastmaster';
import { AddMediaModal } from '@pages/Libraries/components/AddMediaModal/AddMediaModal';
import { useAddVideosModal } from '@pages/Library/context/AddVideosModalContext';
import { useMediaTable } from '@pages/Library/context/MediaTableContext';
import { useMediaStatusPolling } from '@pages/Library/hooks/useMediaStatusPolling';
import { canOpenMediaModal, isProbablyImage } from '@pages/Library/utils/mediaStatus';
import { getOmniFields, getOmniMatches, parseOmniQuery } from '@pages/Library/utils/omniFilter';
import { useLibraryFolder } from '@stores/Library/LibraryFolder';
import { useModalStore } from '@stores/ModalStore';
import { omniFilterPreferences } from '@stores/OmniFilterPreferences';
import { useUploadManager } from '@stores/UploadManager/UploadManager';
import { Media } from '@typings/Media';
import classNames from 'classnames';
import { observer } from 'mobx-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { EmptyFolder, EmptyLibrary, NoResultsForFilter } from '../EmptyLibrary/EmptyLibrary';
import { startMediaDrag } from '../Folders/mediaDrag';
import { useLibraryOmniFilter } from '../LibraryOmniFilter/LibraryOmniFilter';
import { MediaListSkeleton } from '../MediaListSkeleton/MediaListSkeleton';
import {
  ChannelCell,
  DateAddedCell,
  DurationCell,
  FrameCountCell,
  HeightCell,
  IsYouTubeShortCell,
  MediaTableRow,
  MenuCell,
  PublishedDateCell,
  SizeCell,
  StatusCell,
  TitleCell,
  WidthCell,
  YouTubeIdCell,
} from './cells';
import { createColumns, DEFAULT_COLUMN_ORDER, DEFAULT_VISIBILITY } from './columns';
import { MEDIA_TRAILING_COLUMNS } from './columns/ColumnDefinitions';
import {
  useDragDropUpload,
  useMediaData,
  useModalHandlers,
  useSelectionManagement,
  useThumbnailErrors,
  useVideoStats,
} from './hooks';
import css from './LibraryContentMediaTable.module.scss';

const UI_HIGHLIGHT_DURATION_MS = 2000;

export const LibraryContentMediaTable = observer(() => {
  const apiRef = useGridApiRef();
  const folder = useLibraryFolder();
  // The search line above the table (`LibraryToolbar`) is the omnifilter. The table takes its
  // query too, for its "no media match your search" state and the clear button there.
  const omniFilter = useLibraryOmniFilter();
  const modalStore = useModalStore();
  const uploadManager = useUploadManager();
  const addVideosModal = useAddVideosModal();
  const { setFilteredRowCount, setFilteredDuration, setFilteredSize } = useMediaTable();
  const [searchParams, setSearchParams] = useSearchParams();
  const [highlightedMediaId, setHighlightedMediaId] = useState<string | null>(null);

  // Table view previously polled only through StatusCell's per-row hook, so it
  // needs the view-level registration explicitly now that the per-card hook is
  // gone — otherwise this view would never refresh status at all.
  useMediaStatusPolling();

  // State for file upload modal
  const [droppedFiles, setDroppedFiles] = useState<File[]>([]);
  const [droppedRejected, setDroppedRejected] = useState<DragDropRejected[]>([]);
  const droppedRejectedRef = useRef(droppedRejected);
  droppedRejectedRef.current = droppedRejected;

  // Custom hooks for different concerns
  const { data: mediaData, isLoading } = useMediaData();

  // Dynamic column visibility: show date columns based on media origin; at least one is always visible
  const hasAnyPublishedAt = useMemo(
    () => mediaData.some((media) => media.publishedAt != null),
    [mediaData],
  );
  const hasAnyYouTubeId = useMemo(
    () => mediaData.some((media) => getYouTubeVideoId(media as MediaTableRow) != null),
    [mediaData],
  );

  const dynamicVisibility = useMemo<GridColumnVisibilityModel>(
    () => ({
      publishedAt: hasAnyPublishedAt,
      youtubeId: hasAnyYouTubeId,
    }),
    [hasAnyPublishedAt, hasAnyYouTubeId],
  );

  const { thumbnailErrors, handleThumbnailError } = useThumbnailErrors();
  const { handleSelectionChange } = useSelectionManagement(mediaData);
  const videoStats = useVideoStats(mediaData);
  const { addMediaModalRef, handleThumbnailClick, handleTitleClick } = useModalHandlers();

  // Helper to conditionally handle click events based on whether modal can be opened
  const getClickHandler = useCallback(
    (
      canOpen: boolean,
      handler: (media: Media, e: React.MouseEvent<Element>) => void,
    ): ((media: Media, e: React.MouseEvent<Element>) => void) => {
      if (canOpen) return handler;
      return (media: Media, e: React.MouseEvent<Element>) => {
        e.stopPropagation();
        e.preventDefault();
        showToast('media-modal-not-ready', { media });
      };
    },
    [],
  );

  const fileUploadFlow = useFileUploadFlow({
    files: droppedFiles,
    onConfirm: async (selectedFiles) => {
      if (folder?.library?.libraryId) {
        const rejects = droppedRejectedRef.current;
        setDroppedFiles([]);
        setDroppedRejected([]);
        addVideosModal?.closeAddVideosModal();
        uploadManager
          .uploadFiles(
            selectedFiles,
            {
              libraryId: folder.library.libraryId,
              folderPath: folder.currentFolderPath,
            },
            'drag-drop',
            rejects,
          )
          .then(() => uploadManager.refreshLibrary(folder.library.libraryId))
          .catch((error) => console.error('Error uploading files:', error));
      }
    },
    onDismiss: () => {
      const unclaimed = droppedRejectedRef.current;
      if (unclaimed.length > 0) {
        uploadManager.rejectedFiles.push(
          ...unclaimed.map((r) => ({
            file: r.file,
            reason: r.reason,
            category: 'other' as const,
            status: 'failed' as const,
          })),
        );
      }
      setDroppedFiles([]);
      setDroppedRejected([]);
    },
  });

  // Handle dropped files by opening the file upload modal.
  // Appends to existing selection and dedupes by name+size+lastModified.
  // Skip if the add-media modal is open — MediaUploadBox handles drops in that case.
  // Refs keep the callback stable (empty deps) so useDragDropUpload doesn't re-register listeners.
  const modalStoreRef = useRef(modalStore);
  modalStoreRef.current = modalStore;
  const fileUploadFlowOpenRef = useRef(fileUploadFlow.open);
  fileUploadFlowOpenRef.current = fileUploadFlow.open;
  const handleFilesDropped = useCallback((files: File[], rejected: DragDropRejected[]) => {
    // Anywhere in the stack, not just top-most: the file picker legitimately opens on top of
    // add-media, and an exact top-of-stack check would stop matching there and let this handle
    // the drop as well as MediaUploadBox.
    if (modalStoreRef.current.openModalIds.includes('add-media')) return;

    setDroppedFiles((prev) => {
      const existingKeys = new Set(prev.map((f) => `${f.name}-${f.size}-${f.lastModified}`));
      const newFiles = files.filter(
        (f) => !existingKeys.has(`${f.name}-${f.size}-${f.lastModified}`),
      );
      return [...prev, ...newFiles];
    });
    if (rejected.length > 0) {
      setDroppedRejected((prev) => [...prev, ...rejected]);
    }
    queueMicrotask(() => fileUploadFlowOpenRef.current());
  }, []);

  const { isDragging } = useDragDropUpload(handleFilesDropped);

  // Handle sort changes from DataTable and sync to folder store
  const handleSortModelChange = useCallback(
    (newSortModel: GridSortModel) => {
      folder.setSortModel(newSortModel);
    },
    [folder],
  );

  // Handle filter changes from DataTable and sync to folder store
  const handleFilterModelChange = useCallback(
    (newFilterModel: GridFilterModel) => {
      folder.setFilterModel(newFilterModel);
    },
    [folder],
  );

  // Cancel upload: immediately clear and remove from list (no confirmation modal).
  // clearAndCleanupUploads aborts the upload, deletes backend media if created, clears state, and refreshes.
  const handleUploadClearing = useCallback(
    (media: MediaTableRow) => {
      if (!media?.uploadStatus) return;

      const status = media.uploadStatus;
      uploadManager
        .clearAndCleanupUploads([status.uuid], {
          folderStore: folder,
          mode: 'individual',
          showSnackbar: true,
        })
        .catch((err) => {
          console.error('Cancel upload failed:', err);
        });
    },
    [uploadManager, folder],
  );

  // Track filtered row count/duration for header display
  useEffect(() => {
    if (!apiRef.current) return;

    const updateCounts = () => {
      // Update filtered row count and duration for header display using DataTable selector
      // This gets the actual filtered row IDs from the grid state
      try {
        const filteredRowIds = gridFilteredSortedRowIdsSelector(apiRef);
        const filteredCount = filteredRowIds.length;
        setFilteredRowCount(filteredCount);

        // Calculate total duration of filtered items
        const filteredRowIdsSet = new Set(filteredRowIds);
        const filteredDuration = mediaData
          .filter((media) => filteredRowIdsSet.has(media._id))
          .reduce(
            (acc, media) =>
              acc + (isProbablyImage(media) ? 0 : media.metadata?.video?.duration || 0),
            0,
          );
        setFilteredDuration(filteredDuration);

        const filteredSize = mediaData
          .filter((media) => filteredRowIdsSet.has(media._id))
          .reduce((acc, media) => acc + (media.metadata?.video?.originalMediaSize || 0), 0);
        setFilteredSize(filteredSize);
      } catch (error) {
        // Fallback to getRowsCount if selector fails
        const rowCount = apiRef.current ? apiRef.current.getRowsCount() : 0;
        setFilteredRowCount(rowCount);
        // Calculate duration from all visible rows as fallback
        const totalDuration = mediaData.reduce(
          (acc, media) => acc + (isProbablyImage(media) ? 0 : media.metadata?.video?.duration || 0),
          0,
        );
        setFilteredDuration(totalDuration);

        const totalSize = mediaData.reduce(
          (acc, media) => acc + (media.metadata?.video?.originalMediaSize || 0),
          0,
        );
        setFilteredSize(totalSize);
      }
    };

    // Initial call
    updateCounts();

    // Subscribe to state changes (includes filter changes)
    const unsubscribeState = apiRef.current?.subscribeEvent('stateChange', updateCounts);
    // Also subscribe to filter model changes to ensure we update immediately
    const unsubscribeFilter = apiRef.current?.subscribeEvent('filterModelChange', updateCounts);

    return () => {
      if (unsubscribeState) unsubscribeState();
      if (unsubscribeFilter) unsubscribeFilter();
    };
  }, [apiRef, setFilteredRowCount, setFilteredDuration, setFilteredSize, mediaData]);

  // Resolve against the filtered row model. Hidden rows must not leave an animation-frame
  // loop running forever, and the table API owns its scroller (multiple tables can be mounted).
  useEffect(() => {
    const id = searchParams.get('mediaId');
    if (!id || !mediaData.length) return;
    const frame = requestAnimationFrame(() => {
      if (!apiRef.current?.scrollToRow(id)) return;
      setHighlightedMediaId(id);
      const nextParams = new URLSearchParams(searchParams);
      nextParams.delete('mediaId');
      setSearchParams(nextParams, { replace: true });
    });
    return () => {
      cancelAnimationFrame(frame);
    };
  }, [searchParams, mediaData, apiRef, setSearchParams]);

  useEffect(() => {
    if (!highlightedMediaId) return;
    const timeout = setTimeout(() => setHighlightedMediaId(null), UI_HIGHLIGHT_DURATION_MS);
    return () => clearTimeout(timeout);
  }, [highlightedMediaId]);

  // The omnifilter lives in the row data (folder._omniFilter), not the DataTable
  // filterModel, so parse it here to (a) highlight matched tokens in the title and
  // (b) annotate each row with why it matched.
  const parsedOmni = useMemo(
    () => parseOmniQuery(folder?._omniFilter, omniFilterPreferences.matchAll),
    [folder?._omniFilter, omniFilterPreferences.matchAll],
  );

  // Extract highlight terms from all filename filters, plus the omnifilter tokens.
  // HighlightedText only highlights actual substring occurrences, so passing ID-like
  // tokens (which won't appear in a title) is harmless.
  const filenameHighlightTerms = useMemo(() => {
    const items = folder?.filterModel?.items ?? [];
    const columnTerms = items
      .filter((item) => item.field === 'filename' && item.value && typeof item.value === 'string')
      .map((item) => item.value as string);
    return [...columnTerms, ...(parsedOmni?.rawTokens ?? [])];
  }, [folder?.filterModel, parsedOmni]);

  // Extract highlight terms from YouTube ID filters (raw filter values, which may be URLs),
  // plus the omnifilter tokens — so a YouTube ID matched via the omni bar is highlighted in
  // the cell too (the omnifilter isn't in filterModel). YouTubeIdCell resolves each token
  // (exact id/URL → full highlight, otherwise substring), so non-ID tokens are harmless.
  const youtubeIdHighlightTerms = useMemo(() => {
    const items = folder?.filterModel?.items ?? [];
    const columnTerms = items
      .filter((item) => item.field === 'youtubeId' && item.value && typeof item.value === 'string')
      .map((item) => item.value as string);
    return [...columnTerms, ...(parsedOmni?.rawTokens ?? [])];
  }, [folder?.filterModel, parsedOmni]);

  // Memoized render functions
  const renderTitleWithThumbnail = useCallback(
    (params: GridRenderCellParams) => {
      const media = params.row as MediaTableRow;
      const hasThumbnailError = thumbnailErrors.has(media._id);
      const canOpenModal = canOpenMediaModal(media) && !media.isUploading;

      const matches = parsedOmni ? getOmniMatches(getOmniFields(media), parsedOmni) : [];

      return (
        <TitleCell
          media={media}
          hasThumbnailError={hasThumbnailError}
          onThumbnailError={handleThumbnailError}
          onThumbnailClick={getClickHandler(canOpenModal, handleThumbnailClick)}
          onTitleClick={getClickHandler(canOpenModal, handleTitleClick)}
          highlightTerms={filenameHighlightTerms}
          matches={matches}
        />
      );
    },
    [
      thumbnailErrors,
      handleThumbnailError,
      handleThumbnailClick,
      handleTitleClick,
      getClickHandler,
      filenameHighlightTerms,
      parsedOmni,
    ],
  );

  const renderDuration = useCallback((params: GridRenderCellParams) => {
    const media = params.row as MediaTableRow;
    return <DurationCell media={media} />;
  }, []);

  const renderSize = useCallback((params: GridRenderCellParams) => {
    const media = params.row as MediaTableRow;
    return <SizeCell media={media} />;
  }, []);

  const renderDateAdded = useCallback((params: GridRenderCellParams) => {
    const media = params.row as MediaTableRow;
    return <DateAddedCell media={media} />;
  }, []);

  const renderPublishedDate = useCallback((params: GridRenderCellParams) => {
    const media = params.row as MediaTableRow;
    return <PublishedDateCell media={media} />;
  }, []);

  const renderStatus = useCallback((params: GridRenderCellParams) => {
    const media = params.row as MediaTableRow;
    return <StatusCell media={media} />;
  }, []);

  const renderFrameCount = useCallback((params: GridRenderCellParams) => {
    const media = params.row as MediaTableRow;
    return <FrameCountCell media={media} />;
  }, []);

  const renderWidth = useCallback((params: GridRenderCellParams) => {
    const media = params.row as MediaTableRow;
    return <WidthCell media={media} />;
  }, []);

  const renderHeight = useCallback((params: GridRenderCellParams) => {
    const media = params.row as MediaTableRow;
    return <HeightCell media={media} />;
  }, []);

  const renderChannel = useCallback((params: GridRenderCellParams) => {
    const media = params.row as MediaTableRow;
    return <ChannelCell media={media} />;
  }, []);

  const renderYouTubeId = useCallback(
    (params: GridRenderCellParams) => {
      const media = params.row as MediaTableRow;
      return <YouTubeIdCell media={media} highlightTerms={youtubeIdHighlightTerms} />;
    },
    [youtubeIdHighlightTerms],
  );

  const renderIsYouTubeShort = useCallback((params: GridRenderCellParams) => {
    const media = params.row as MediaTableRow;
    return <IsYouTubeShortCell media={media} />;
  }, []);

  const renderMenu = useCallback(
    (params: GridRenderCellParams) => {
      const media = params.row as MediaTableRow;
      return (
        <MenuCell
          media={media}
          onUploadClear={(e: React.MouseEvent) => {
            e.stopPropagation();
            e.preventDefault();
            handleUploadClearing(media);
          }}
        />
      );
    },
    [handleUploadClearing],
  );

  // Memoize columns to prevent recreation on every render
  const columns: GridColDef[] = useMemo(
    () =>
      createColumns({
        renderFunctions: {
          renderTitleWithThumbnail,
          renderDuration,
          renderSize,
          renderDateAdded,
          renderPublishedDate,
          renderStatus,
          renderFrameCount,
          renderWidth,
          renderHeight,
          renderChannel,
          renderMenu,
          renderYouTubeId,
          renderIsYouTubeShort,
        },
        videoStats,
        DurationFilterInputValue: DurationFilterInput,
        getGridNumericOperators,
        getGridSingleSelectOperators,
      }),
    [
      videoStats,
      renderTitleWithThumbnail,
      renderChannel,
      renderDuration,
      renderSize,
      renderFrameCount,
      renderWidth,
      renderHeight,
      renderStatus,
      renderDateAdded,
      renderPublishedDate,
      renderMenu,
      renderYouTubeId,
      renderIsYouTubeShort,
    ],
  );

  // Shared preference schema preserves existing saved visibility and order.
  const columnPreferences = useGridColumnPreferences({
    tableId: 'libraryMedia',
    apiRef,
    columns,
    defaultOrder: DEFAULT_COLUMN_ORDER,
    defaultVisibility: DEFAULT_VISIBILITY,
    dynamicVisibility,
    // `filename` is the table's identity column: pinned at index 0, and `disableReorder`.
    anchorField: 'filename',
    trailingFields: MEDIA_TRAILING_COLUMNS,
  });

  // A row drags onto a folder like a grid card does. Rows that are still uploads have no media to
  // move — their id is the upload's until the backend has one — so they refuse the drag.
  const rowDragProps = useMemo(() => {
    if (folder.library.isReadOnly || folder.folders.length === 0) return undefined;
    return {
      draggable: true,
      onDragStart: (event: React.DragEvent<HTMLDivElement>) => {
        const id = event.currentTarget.dataset.id;
        if (id && folder.allFiles.some((file) => file._id === id)) {
          startMediaDrag(event, id, folder);
        } else {
          event.preventDefault();
        }
      },
    };
  }, [folder, folder.library.isReadOnly, folder.folders.length]);

  if (!folder) return null;

  // Get row className to mark highlighted rows
  const getRowClassName = useCallback(
    (params: { id: string | number }) => {
      return highlightedMediaId === params.id ? 'highlighted-row' : '';
    },
    [highlightedMediaId],
  );

  return (
    <>
      <div className={css.dataGridContainer}>
        <DragDropOverlay isDragging={isDragging} />
        <DataTable
          apiRef={apiRef}
          searchValue={omniFilter.applied}
          onSearchChange={omniFilter.onChange}
          rows={mediaData}
          columns={columnPreferences.columns}
          className={classNames(css.dataGrid, {
            [css.hasHighlight]: highlightedMediaId !== null,
            [css.headerless]: folder.holdsOnlyFolders && mediaData.length === 0,
          })}
          rowHeight={parsedOmni ? 108 : 86}
          sortingOrder={['asc', 'desc']}
          sortModel={folder.effectiveSortModel}
          onSortModelChange={handleSortModelChange}
          filterModel={folder.filterModel}
          onFilterModelChange={handleFilterModelChange}
          sortingMode="server"
          checkboxSelection
          disableRowSelectionOnClick={false}
          getRowId={(row) => row._id}
          getRowClassName={getRowClassName}
          onRowSelectionModelChange={handleSelectionChange}
          rowSelectionModel={{
            type: 'include',
            ids: new Set(Array.from(folder.videoSelection.selectedItems)),
          }}
          getRowSpacing={(params) => ({
            top: params.isFirstVisible ? 0 : 5,
            bottom: params.isLastVisible ? 0 : 5,
          })}
          slots={{
            loadingOverlay: () => <MediaListSkeleton variant="table" />,
            noRowsOverlay: () => (
              <div className={css.emptyLibraryContainer}>
                {folder.showsEmptyLibrary ? (
                  <EmptyLibrary library={folder.library} />
                ) : (
                  <EmptyFolder library={folder.library} />
                )}
              </div>
            ),
            noResultsOverlay: () => (
              <div className={css.emptyLibraryContainer}>
                <NoResultsForFilter
                  clearFilter={() => {
                    folder.setFilterModel({ items: [] });
                  }}
                />
              </div>
            ),
          }}
          slotProps={{ row: rowDragProps }}
          columnVisibilityModel={columnPreferences.columnVisibilityModel}
          onColumnVisibilityModelChange={columnPreferences.onColumnVisibilityModelChange}
          onColumnOrderChange={columnPreferences.onColumnOrderChange}
          getTogglableColumns={columnPreferences.getTogglableColumns}
          // Only the first load: a background refetch keeps the rows it has (the store keeps its
          // data while fetching), and covering them with a placeholder on every refetch would
          // blank a table the user is reading.
          loading={isLoading && folder.data === null}
        />
      </div>
      <AddMediaModal
        ref={addMediaModalRef}
        destination={{
          libraryId: folder?.library?.libraryId || '',
          folderPath: folder.currentFolderPath,
        }}
        externalIsDragging={isDragging}
      />
      {fileUploadFlow.flowState === 'selecting' && (
        <ChooseFileUploadModal {...fileUploadFlow.selectionProps} open={true} />
      )}
      <ChooseFileUploadModalSummaryConfirm {...fileUploadFlow.summaryProps} />
    </>
  );
});
